// @flow
// vendor libs
import contextEvent from 'app/common/lib/ContextEvent';
import contextLogger from 'app/common/lib/ContextLogger';
import errorUtil from 'app/common/lib/errorUtil';
import Facebook from 'app/common/lib/Facebook';
import FacebookDialog from 'app/common/lib/FacebookDialog';
import ga from 'app/common/lib/ga';
import i18n from 'app/common/lib/i18n';
import trackRegisterAction from 'app/common/lib/util/trackRegisterAction';
import user from 'app/common/lib/User';

/**
 * @type {string}
 */
const FB_PUBLISH_ACTIONS_PERMISSION = 'publish_actions';

const FbUtil = {};

/**
 * Check if the user has already authorized Pinterest to
 * publish_actions to Timeline. If yes, call the success callback, and
 * if not, call the error callback.
 *
 * @param {{
 *        error: (function()|undefined),
 *        success: (function()|undefined),
 *        complete: (function()|undefined )
 *        }} callback
 */
FbUtil.checkFbTimelinePermission = function(callback: *) {
  const fbCallback = function(_fb) {
    _fb.api('/me/permissions', (perms_response) => {
      const permissions = perms_response.data;
      let publishPermissionGranted = false;
      if (permissions) {
        const permissionFound = permissions.find(
          (permission) =>
            permission.permission === FB_PUBLISH_ACTIONS_PERMISSION &&
            permission.status === 'granted',
        );
        publishPermissionGranted = !!permissionFound;
      }

      if (publishPermissionGranted) {
        if (callback.success) {
          callback.success();
        }
      } else if (callback.error) {
        callback.error();
      }
    });
  };
  Facebook.ensureInit(fbCallback, user);
};

/**
 * Check if the account connected to facebook matches the current Facebook account
 * that the user is logged into
 *
 * @param {FB} _fb
 * @param {string=} facebookId
 * @private
 */
FbUtil.fbConnectedAccountMatchesLoggedInAccount = function(_fb: *, facebookId: *) {
  if (!facebookId) {
    return true;
  }
  const authResponse = _fb.getAuthResponse();
  if (!authResponse) {
    return true;
  }
  return facebookId === authResponse.userID;
};

/**
 * If we don't already have it, request the Facebook permission to
 * publish to Timeline.
 *
 * @param {{
 *        error: (function()|undefined),
 *        success: (function()|undefined),
 *        complete: (function()|undefined )
 *        }} callback
 * @param {string=} facebookId
 */
type RequestHandlers = {
  error?: () => mixed,
  success?: () => mixed,
  complete?: () => mixed,
};
FbUtil.maybeRequestFbTimelinePermission = function(callback: RequestHandlers, facebookId: *) {
  Facebook.ensureInit((_fb) => {
    const match = FbUtil.fbConnectedAccountMatchesLoggedInAccount(_fb, facebookId);
    if (!match) {
      errorUtil.showError(
        i18n._(
          'Oops! Your account is linked to a different Facebook account ' +
            "than the one you're logged into now.",
        ),
      );
      if (callback.error) {
        callback.error();
      }
      if (callback.complete) {
        callback.complete();
      }
      return;
    }

    FbUtil.checkFbTimelinePermission({
      success() {
        // already have permission
        if (callback.success) {
          callback.success();
        }
        if (callback.complete) {
          callback.complete();
        }
      },
      error() {
        // need to request permission
        FbUtil.fbLogin(callback);
      },
    });
  }, user);
};

/**
 * Open a Facebook popup window and request the user for permission
 * @param {{
 *        error: (function()|undefined),
 *        complete: (function()|undefined),
 *        success: (function()|undefined)
 *        }} callback
 */
FbUtil.fbLogin = function(callback: *) {
  Facebook.ensureInit((_fb) => {
    _fb.login(
      (response) => {
        if (response.authResponse) {
          FbUtil.checkFbTimelinePermission(callback);
        } else if (callback.error) {
          callback.error();
        }
        if (callback.complete) {
          callback.complete();
        }
      },
      { scope: FB_PUBLISH_ACTIONS_PERMISSION },
    );
  }, user);
};

/**
 * Starts a Facebook share flow. This mainly just wraps the Facebook Dialog open with a variety
 * of tracking.
 * @param {{dialog_options:Object,
 *          object_type:string,
 *          object_id:string,
 *          ga_category:(undefined|string)}} options
 *  object_type Either a pin, board, or pinner.
 *  object_id The id of the pin, board, or pinner.
 *  ga_category The Google Analytics label to apply.
 */
FbUtil.shareFb = function(options: *) {
  const GACategory = options.ga_category || 'share_facebook';
  ga.trackEvent(GACategory, PCONST.GA_ACTION_CLICK);

  const dialogOptions = options.dialog_options || {};
  dialogOptions.callback = function() {
    // As of 5/18/15:
    // not currently working because the callback is not called when type === 'feed' in FacebookDialog.js.
    // we use sharer.php instead which does not allow for callbacks
    // querying for this context log event will only have results in Android

    const _contextEvent = contextEvent.fromEventType(PCONST.thrift.EventType.PIN_SHARE_FACEBOOK);
    if (options.object_id) {
      _contextEvent.setObjectIdStr(options.object_id);
    }
    contextLogger.getInstance().addEvent(_contextEvent);
  };

  const object_type = dialogOptions.object_type;
  if (object_type) {
    // used for funnel tracking
    trackRegisterAction('facebook_share_dialog_open_' + object_type);
  }

  FacebookDialog.open(dialogOptions);
};

/**
 * Returns the locale string in Facebook format, e.g. en_US, it_IT.
 *
 * @param contextLocale:String the locale string, e.g. this.context.locale
 */
FbUtil.getLocaleString = function(contextLocale: string) {
  let localeString = contextLocale;
  localeString = localeString.replace(/-/g, '_');
  switch (localeString) {
    case 'de':
      localeString = 'de_DE';
      break;
    case 'fr':
      localeString = 'fr_FR';
      break;
    case 'it':
      localeString = 'it_IT';
      break;
    case 'ja':
      localeString = 'ja_JP';
      break;
    case 'nl':
      localeString = 'nl_NL';
      break;
    case 'tr':
      localeString = 'tr_TR';
      break;
    case 'en_AU':
      localeString = 'en_US';
      break;
    case 'es_419':
      localeString = 'es_LA';
      break;
    case 'es_AR':
      localeString = 'es_LA';
      break;
    default:
      break;
  }
  return localeString;
};

export default FbUtil;
